import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { ArrowRightIcon } from '@/ui/Icons/ArrowRightIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { XIcon } from '@/ui/Icons/XIcon';

// enums
import { MenuId, MenuSettings } from './index';

interface HeaderProps {
  /** ID of the current network. */
  chainId: number;
  /** Destination chain ID for cross-chain settings. */
  destinationChainId?: number;
  /** ID of the current settings menu. */
  currentMenuId: MenuId;
  /** The current settings menu. */
  currentMenu: MenuSettings;
  /** Callback to update the current menu. */
  setCurrentMenuId: (newId: MenuId) => void;
}

/**
 * Trade settings header.
 */
export const Header = ({
  chainId,
  destinationChainId,
  currentMenuId,
  currentMenu,
  setCurrentMenuId,
}: HeaderProps) => {
  const showBackButton = currentMenuId !== MenuId.MAIN;
  const showChainLabel = currentMenuId === MenuId.MAIN;

  return (
    <div className="flex items-center pt-6 pb-5 tablet:pb-0 px-4 tablet:px-6 w-full h-14 tablet:h-20 z-10 tablet:flex-col tablet:items-start">
      <div className="flex items-center justify-between w-full">
        <Dialog.Title className={fontStyles({ variant: 'H5' })}>
          {showBackButton && (
            <button
              className={buttons({
                size: 'small',
                shape: 'rounded',
                color: 'transparent',
                iconOnly: true,
                class:
                  'w-9 absolute top-3.5 left-3.5 flex items-center justify-center duration-200 transition-all',
              })}
              onClick={() => setCurrentMenuId(MenuId.MAIN)}
            >
              <ChevronDownIcon className="transform rotate-90" />
            </button>
          )}

          <span className={showBackButton ? 'ml-10' : undefined}>{currentMenu.title}</span>
        </Dialog.Title>

        <VisuallyHidden asChild>
          <Dialog.Description>{currentMenu.description}</Dialog.Description>
        </VisuallyHidden>

        <Dialog.Close
          className={buttons({
            size: 'small',
            shape: 'rounded',
            color: 'transparent',
            iconOnly: true,
            class: '-mr-3 -my-2 top-6 absolute right-7 hidden tablet:block',
          })}
          aria-label="close trade settings dialog"
        >
          <XIcon aria-hidden className="h-4 w-4" />
        </Dialog.Close>
      </div>

      {showChainLabel && (
        <div
          className={fontStyles({
            variant: 'LABEL_SM',
            class: 'text-token-gray-800 flex items-center gap-1.5 mt-1 min-w-0 max-w-full',
          })}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <ChainAvatar
              chainId={chainId}
              alt={`${NETWORK_NAME_PER_CHAIN_ID[chainId]} logo`}
              size={14}
              className="shrink-0"
            />
            <span className="truncate">{NETWORK_NAME_PER_CHAIN_ID[chainId]}</span>
          </span>
          {destinationChainId != null && (
            <>
              <ArrowRightIcon
                aria-hidden
                focusable={false}
                className="shrink-0"
                width={14}
                height={14}
              />
              <span className="flex items-center gap-1.5 min-w-0">
                <ChainAvatar
                  chainId={destinationChainId}
                  alt={`${NETWORK_NAME_PER_CHAIN_ID[destinationChainId]} logo`}
                  size={14}
                  className="shrink-0"
                />
                <span className="truncate">{NETWORK_NAME_PER_CHAIN_ID[destinationChainId]}</span>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
